import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BandReveal, InteractiveDots, Pressable, ScrollReveal, TextReveal } from '@/components/motion'
import { PlatformIndex, ServiceFaq } from '@/components/landing/service'
import { platformFeaturesList, type PlatformFeature } from '@/data/platform'

export interface LayoutProps {
  feature: PlatformFeature
}

/** Split a heading so its last word can sit in a highlight box. */
export function splitLastWord(text: string): [string, string] {
  const i = text.lastIndexOf(' ')
  return i === -1 ? ['', text] : [text.slice(0, i), text.slice(i + 1)]
}

/** Every platform page, for the "Explore the platform" index. */
export const platformIndexItems = platformFeaturesList.map((f) => ({ slug: f.slug, label: f.label }))

/** Muted body copy with the reference's quote bar. */
export const quoteBar =
  'border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]'

export const ink = 'text-[var(--color-landing-gray-900)] dark:text-white'
export const muted = 'text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]'
export const hairline = 'border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]'

/**
 * The page's primary action. `surface` picks the variant that holds contrast:
 * default ink button on white, white button on black, sweep-safe on colour.
 */
export function TrialButton({
  surface = 'light',
  size = 'lg',
  className,
}: {
  surface?: 'light' | 'dark' | 'color'
  size?: 'md' | 'lg'
  className?: string
}) {
  return (
    <Pressable className={cn('inline-block shrink-0', className)}>
      <Link
        to="/register"
        className={cn(
          'landing-cta landing-cta--solid landing-btn-sweep',
          size === 'lg' ? 'landing-cta--lg' : 'landing-cta--md',
          surface === 'dark' && '!border-white !bg-white !text-[var(--color-landing-primary)] hover:!text-white',
          surface === 'color' && 'landing-cta--on-color'
        )}
      >
        Start free trial
        <ArrowRight aria-hidden className="h-5 w-5" />
      </Link>
    </Pressable>
  )
}

/** FAQ beside the platform index (shared by most layouts). */
export function PlatformFaq({ feature, className }: LayoutProps & { className?: string }) {
  return (
    <ServiceFaq
      className={className}
      items={feature.faq}
      aside={<PlatformIndex current={feature.slug} items={platformIndexItems} />}
    />
  )
}

export type CtaTone = 'blue' | 'purple' | 'pink' | 'dark'

const CTA_BG: Record<CtaTone, string> = {
  blue: 'bg-[var(--color-landing-accent)]',
  purple: 'bg-[var(--color-landing-secondary)]',
  pink: 'bg-[#ff00f2]',
  dark: 'bg-[var(--color-landing-primary)] dark:bg-black',
}

/**
 * Closing band, coloured per page: hero-size heading, then the two actions and
 * the description side by side. Pink carries ink text (white fails contrast on
 * #ff00f2); the other tones carry white.
 */
export function PlatformCtaBand({ feature, tone }: LayoutProps & { tone: CtaTone }) {
  const inkBand = tone === 'pink'
  return (
    <BandReveal>
      <section
        aria-label={feature.ctaTitle}
        className={cn('landing-section-lg relative overflow-hidden', CTA_BG[tone], inkBand ? 'text-[#0a0a0a]' : 'text-white')}
      >
        {tone !== 'dark' && <InteractiveDots />}
        <div className="landing-container relative z-10">
          <TextReveal as="h2" className={cn('landing-text-hero max-w-[18ch]', inkBand ? 'text-[#0a0a0a]' : 'text-white')}>
            {feature.ctaTitle}
          </TextReveal>
          <ScrollReveal direction="none" delay={0.2}>
            <div className="mt-12 flex flex-col gap-8 md:mt-16 lg:flex-row lg:items-center lg:gap-16">
              <div className="flex flex-wrap items-center gap-4">
                <Pressable className="inline-block">
                  <Link
                    to="/register"
                    className={cn(
                      'landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep',
                      inkBand
                        ? '!border-[#0a0a0a] !bg-[#0a0a0a] !text-white [--sweep-color:var(--color-landing-accent)]'
                        : tone === 'dark'
                          ? '!border-white !bg-white !text-[var(--color-landing-primary)] hover:!text-white'
                          : 'landing-cta--on-color'
                    )}
                  >
                    Start free trial
                    <ArrowRight aria-hidden className="h-5 w-5" />
                  </Link>
                </Pressable>
                <Link
                  to="/contact"
                  className={cn(
                    'landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep',
                    inkBand
                      ? '!border-[#0a0a0a] !text-[#0a0a0a] hover:!text-white'
                      : 'landing-cta--on-color'
                  )}
                >
                  Book a demo
                </Link>
              </div>
              <p className={cn('max-w-[45ch] landing-text-body-lg', inkBand ? 'text-[#0a0a0a]/80' : 'text-white/85')}>
                {feature.ctaDescription}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </BandReveal>
  )
}

/** Section heading whose last word sits in a highlight box. */
export function HighlightHeading({
  text,
  color = 'blue',
  as = 'h2',
  id,
  className,
}: {
  text: string
  color?: 'blue' | 'purple' | 'pink'
  as?: 'h2' | 'h3'
  id?: string
  className?: string
}) {
  const [lead, last] = splitLastWord(text)
  const bg =
    color === 'pink' ? 'bg-[#ff00f2]' : color === 'purple' ? 'bg-[var(--color-landing-secondary)]' : 'bg-[var(--color-landing-accent)]'
  return (
    <TextReveal as={as} className={cn('landing-text-display-xl', ink, className)}>
      <span id={id}>
        {lead}{lead ? ' ' : ''}
        <span className={cn('landing-highlight', bg)}>{last}</span>
      </span>
    </TextReveal>
  )
}
